/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import React, { useContext, useLayoutEffect, useState } from 'react';
import { DataContext } from '../contexts/DataContext';
import { handleKeyDownSubmission } from '../utils/cs-helpers';
import LocationResultCardMarker from './cs/LocationResultCarousel/LocationResultCardMarker';

const MapMarker = ({ locationIndex, totalMarkerCount, resetMapCenter }) => {
  const MARKER_NUMBER = locationIndex + 1;
  const SELECTED_FILL = 'var(--tertiary-brand-color)';
  const NORMAL_FILL = 'var(--primary-brand-color)';
  const { selectedLocationIndex, setSelectedLocationIndex } = useContext(DataContext);
  const [zIndex, setZIndex] = useState(1000 - locationIndex); // closest location has collision priority to start

  useLayoutEffect(() => {
    determineMarkerSelectedOrDefault();
  }, [selectedLocationIndex]);

  const determineMarkerSelectedOrDefault = () => {
    if (isMarkerSelected()) {
      setMarkerSelected();
      resetMapCenter(locationIndex);
    } else {
      setDefaultMarker();
    }
  };

  const handleMarkerHoverOrFocus = () => {
    setZIndex(1000 + totalMarkerCount + MARKER_NUMBER); // focused/hovered marker is always top priority on collisions
  };

  const handleMarkerSelection = () => {
    if (isMarkerSelected()) {
      setDefaultMarker();
      setSelectedLocationIndex(null);
    } else {
      setMarkerSelected();
      resetMapCenter(locationIndex);
      setSelectedLocationIndex(locationIndex);
    }
  };

  const isMarkerSelected = () => selectedLocationIndex === locationIndex;

  const setDefaultMarker = () => {
    setZIndex(1000 - locationIndex); // reset to original collision priority
  };

  const setMarkerSelected = () => {
    setZIndex(1000 + totalMarkerCount); // selected marker is collision priority
  };

  return (
    <span
      onBlur={determineMarkerSelectedOrDefault}
      onFocus={handleMarkerHoverOrFocus}
      onClick={handleMarkerSelection}
      onKeyDown={e => handleKeyDownSubmission(e, handleMarkerSelection, locationIndex)}
      onMouseEnter={handleMarkerHoverOrFocus}
      onMouseLeave={determineMarkerSelectedOrDefault}
      className="map-marker-container"
      style={{ zIndex }}
      role="button"
      tabIndex={0}
      aria-label={`location ${MARKER_NUMBER}`}
      id={`map-marker-${MARKER_NUMBER}`}
    >
      <LocationResultCardMarker
        pathFill={isMarkerSelected() ? SELECTED_FILL : NORMAL_FILL}
        locationIndex={locationIndex}
      />
    </span>
  );
};

export default React.memo(MapMarker);
